// The typed client for /v1/admin (types generated from the admin API's OpenAPI document,
// src/gen/api.ts). The admin console sends only its own session cookie (`credentials:
// 'include'`): same origin locally (the Vite proxy), VITE_API_URL in production, where the API
// allows the admin origin only (apps/api/src/admin/routes.ts).
import createClient from 'openapi-fetch'
import type { paths } from '../gen/api'

export const API_BASE = `${(import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')}/v1/admin`

export const api = createClient<paths>({
  baseUrl: API_BASE,
  credentials: 'include',
  // Every admin call carries it; the API refuses calls without it, so no other site can make a
  // staff browser call the admin API, GETs included (a custom header needs a CORS preflight).
  headers: { 'Apsio-Admin': '1' },
  // Looked up on each call, so tests can replace it.
  fetch: (request: Request) => globalThis.fetch(request),
})

/** An error answer from the API, with its code (`mfa_required`, `session_expired`...). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** The data of an openapi-fetch result, or an ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.response.ok && result.error === undefined) return result.data as T
  const error = (result.error as { error?: { code?: unknown; message?: unknown } } | undefined)
    ?.error
  throw new ApiError(
    result.response.status,
    typeof error?.code === 'string' ? error.code : 'unknown',
    typeof error?.message === 'string' ? error.message : result.response.statusText,
  )
}

export const isApiError = (error: unknown, status?: number): error is ApiError =>
  error instanceof ApiError && (status === undefined || error.status === status)

/** The header a read-only view carries: staff's reason, URI-encoded (it may be Portuguese). */
export const reasonHeader = (reason: string) => ({
  'apsio-admin-reason': encodeURIComponent(reason),
})

/** A path inside the admin console to return to after sign-in: never another origin. */
export function safeReturnTo(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.startsWith('/')) return undefined
  if (value.startsWith('//') || value.startsWith('/\\')) return undefined
  return value
}

/** A store link to open: only https, as applicants were told; anything else shows as text. */
export function httpsUrl(value: string): string | undefined {
  try {
    return new URL(value).protocol === 'https:' ? value : undefined
  } catch {
    return undefined
  }
}
